import { colors, getPictures, hexToRgb, sendError } from "racing-renderings-library/src/utils";
import { getRaceData, UNAUTHORIZED_EXCEPTION_MESSAGE } from "../../restAPI";
import { getRoundedPhoto } from "../../utils";

const parseTyre = (tyre) => {
  switch (tyre) {
    case "S":
    case "Soft":
      return "softTyre";
    case "M":
    case "Medium":
      return "mediumTyre";
    case "H":
    case "Hard":
      return "hardTyre";
    case "I":
    case "Intermediate":
      return "intermediateTyre";
    case "W":
    case "Wet":
      return "wetTyre";
    default:
      break;
  }
};
const parseRaceData = async (data, icons) => {
  try {
    const meta = data.raceMeta;
    const race = data.race;
    const finish = data.finish;

    const lapTotal = race.lapTotal;
    const chartData = [];
    const [flags, carsImg] = await Promise.all([
      getPictures(
        finish.details
          .filter((d) => d.nationality?.name !== undefined && d.nationality?.picture !== undefined)
          .map((d) => ({ id: d.nationality.name, src: d.nationality.picture }))
      ),
      getPictures(finish.details.map((d) => ({ id: d.team.uuid, src: d.team.carIcon }))),
    ]);
    const countryFlag = meta?.country?.picture ? await getRoundedPhoto(meta.country.picture) : null;

    // Создаю серии данных
    finish.details.forEach((entry, i) => {
      const driver = entry.drivers[0];
      const uuid = driver.uuid;
      const code = driver.shortCode ? driver.shortCode : driver.lastName.substring(0, 3).toUpperCase();
      const grid = entry.gridPosition;
      const pts = entry.points !== undefined ? entry.points : "-";
      const flag = flags.find((f) => f.id === entry.nationality?.name);
      const color = hexToRgb(driver.colour ? driver.colour : entry.team.colour);
      const carImg = carsImg.find((c) => c.id === entry.team.uuid)?.img;
      const retire = finish.retirements.find((d) => d.driver.uuid === uuid);
      const markers = [];
      if (entry.best?.fastest)
        markers.push({
          type: "fastestLap",
          x: entry.best.lap,
        });
      const resultDriverData = {
        uuid,
        name: driver.name,
        code,
        flag,
        carNumber: entry.carNumber,
        color: color ? color : colors[i % colors.length],
        carImg: carImg !== undefined ? carImg : icons.cars[i % icons.cars.length],
        grid,
        finishPos: entry.finishPosition,
        classifiedStatus: entry.classifiedStatus,
        lastLap: entry.laps,
        bestTime: entry.best?.time,
        time: 0,
        pts,
        markers,
        startTyre: undefined,
        tyreStrategy: [],
        pitstops: [],
        data: [{ lap: 0, pos: grid, lapTime: 0, time: 0, gap: 0, lapsToLead: 0, pit: 0 }],
      };
      if (retire !== undefined) {
        const type = retire.type.toLowerCase();
        resultDriverData.retirement = {
          type,
          x: retire.lap,
          marker: icons[type],
        };
      }
      const raceEntriesInfo = race.entries.find((e) => e.driver.uuid === uuid);
      if (raceEntriesInfo !== undefined) resultDriverData.entryUuid = raceEntriesInfo.uuid;
      chartData.push(resultDriverData);
    });

    // Заполняю данные
    race.laps.forEach((lap) => {
      const lapNumber = lap.lap;
      lap.carPosition.forEach((e) => {
        const driver = chartData.find((d) => d.entryUuid === e.entryUuid);
        if (driver === undefined) return;
        if (e.tyreDetail.length > driver.tyreStrategy.length) {
          const type = e.tyreDetail[e.tyreDetail.length - 1].type;
          const tyre = {
            type: parseTyre(type),
            lap: lapNumber,
          };
          driver.tyreStrategy.push(tyre);
          if (lapNumber === 0) driver.startTyre = tyre;
        }
        if (e.pit) driver.pitstops.push(lapNumber);
        let lastLapNum = driver.data[driver.data.length - 1].lap;
        if (lapNumber === 0) {
          const gridLap = driver.data[0];
          if (!driver.grid) driver.grid = e.position;
          gridLap.pas = e.position;
          gridLap.lapTime = e.time;
          gridLap.time = driver.time;
          gridLap.gap = e.gap.timeToLead;
          gridLap.lapsToLead = e.gap.lapsToLead;
          return;
        }

        // заглушка, если нет круга в данных
        if (lastLapNum === lapNumber) return;
        while (lastLapNum < lapNumber - 1) {
          lastLapNum++;
          driver.data.push({
            ...driver.data[driver.data.length - 1],
            lap: lastLapNum,
            absent: true,
          });
        }

        // driver.lastLap = lapNumber
        driver.time += e.time;
        driver.data.push({
          lap: lapNumber,
          pos: e.position,
          lapTime: e.time,
          time: driver.time,
          gap: e.gap.timeToLead,
          lapsToLead: e.gap.lapsToLead,
          pit: driver.pitstops.length,
        });
      });
    });

    // Фиксим пропущенные последние круги
    if (race.laps[race.laps.length - 1].lap < lapTotal) {
      chartData.forEach((driver) => {
        if (driver.lastLap === driver.data.length - 1) return;
        while (driver.data.length < driver.lastLap + 1) {
          driver.data.push({
            ...driver.data[driver.data.length - 1],
            lap: driver.data.length,
            absent: true,
          });
        }
      });
    }

    //Фиксим время гонщикам с пропущенными кругами
    chartData.forEach((driver) => {
      const finishInfo = finish.details.find((e) => e.drivers[0].uuid === driver.uuid);
      const correctTime = finishInfo.time;
      if (driver.time === correctTime) return;
      const absentLaps = driver.data.reduce((p, c) => {
        if (c.absent) return p + 1;
        return p;
      }, 0);
      const lapTime = Math.floor((correctTime - driver.time) / absentLaps);
      driver.time = 0;
      driver.data.forEach((l) => {
        if (l.absent) {
          l.lapTime = lapTime;
        }
        driver.time += l.lapTime;
        l.time = driver.time;
      });
    });

    // Позиция для DNF
    for (const driver of chartData) {
      if (driver.retirement !== undefined && driver.retirement.x > driver.data.length - 1)
        driver.retirement.x = driver.data.length - 1;
      if (driver.classifiedStatus !== "CLA") {
        let pos = 1;
        for (const driv of chartData) {
          if (driv.uuid === driver.uuid) continue;
          driv.data.length > driver.data.length && (pos += 1);
          if (driv.data.length === driver.data.length) {
            if (driv.data[driv.data.length - 1].time === driver.data[driver.data.length - 1].time) {
              driv.grid < driver.grid && (pos += 1);
            } else driv.data[driv.data.length - 1].time < driver.data[driver.data.length - 1].time && (pos += 1);
          }
        }
        driver.finishPos = pos;
      }
    }

    // Мин\Макс время гонщиков
    const times = [{ lap: 0, time: 0 }];
    for (let i = 1; i <= lapTotal; i++) {
      for (const driver of chartData) {
        if (driver.data[i] !== undefined && driver.data[i].pos === 1) {
          times.push({ lap: i, time: driver.data[i].time });
        }
      }
    }
    const raceTime = {
      min: chartData.find((d) => d.lastLap === lapTotal).time,
      max: 0,
      lapTimes: times,
    };
    chartData.forEach((driver) => {
      if (driver.lastLap === lapTotal && driver.time < raceTime.min) raceTime.min = driver.time;
      if (driver.time > raceTime.max) raceTime.max = driver.time;
    });

    // Добавляю круги для CLA
    for (const driver of chartData) {
      if (driver.lastLap < lapTotal && driver.retirement === undefined) {
        if (driver.classifiedStatus === "CLA") {
          const finData = finish.details.find((drv) => drv.drivers[0].uuid === driver.uuid);
          const lapsToLead = finData.gap.lapsToLead;
          const pit = driver.pitstops.length;
          const del = lapTotal - driver.lastLap; // сколько кругов не хватает
          const lapTime = (raceTime.max - driver.time) / del;
          for (let i = 1; i <= del; i++) {
            driver.data.push({
              lap: driver.lastLap + i,
              pos: driver.finishPos,
              lapTime,
              time: raceTime.max,
              gap: 0,
              lapsToLead,
              pit: driver.pitstops.length,
            });
          }
        } else {
          driver.retirement = {
            x: driver.lastLap,
          };
        }
      }
    }

    return { lapTotal, drivers: chartData, raceTime, countryFlag };
  } catch (e) {
    console.error(e);
    sendError(e);
    return null;
  }
};

export const getData = async (id, icons) => {
  const error = { error: "Oops! Something went wrong." };
  const data = await getRaceData(id);

  if (data === UNAUTHORIZED_EXCEPTION_MESSAGE) return { error: UNAUTHORIZED_EXCEPTION_MESSAGE };
  if (
    data === null ||
    data.raceMeta === null ||
    data.raceMeta.error !== undefined ||
    data.race === null ||
    data.race.error !== undefined ||
    data.finish === null ||
    data.finish.error !== undefined
  ) {
    sendError({ name: "Fetch race data error" });
    return error;
  }
  // console.log(data)
  const meta = data.raceMeta;
  let shortTitle = `${meta.series.shortName ?? meta.series.name} - ${meta.season.year}\n${
    meta.event.shortName ?? meta.event.name
  } - race`;
  const title = {
    shortTitle,
    name: meta.series.name + " " + meta.season.name + " " + meta.event.name,
    date: parseInt(data.raceMeta.startTime + "000"),
  };
  return parseRaceData(data, icons).then((parse) => {
    if (parse) return { ...parse, title };
    else {
      sendError({ name: "Parse race data error", message: "an error occurred while processing the data" });
      return error;
    }
  });
};
